import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { DB, FUNCTIONS } from 'src/contexts/FirebaseContext';
import {
  OrgStripeAccount,
  CreateOrgStripeAccountRequest,
  CreateOrgStripeAccountResponse,
  UpdateOrgStripeAccountRequest,
  SetPrimaryStripeAccountRequest,
  DeactivateStripeAccountRequest,
  SyncStripeAccountRequest,
  StripeAccountDetailsResponse,
  StripeAccountsDashboardData,
  GetActiveAccountRequest,
  GetActiveAccountResponse,
  StripeAccountStatus,
} from 'src/@types/organizationStripe';

/**
 * Organization Stripe Connect API
 *
 * Manages multiple Stripe Connect accounts at the organization level.
 * Supports legacy user-level accounts as fallback.
 */
export const organizationStripeApi = createApi({
  reducerPath: 'organizationStripeApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['OrgStripeAccounts', 'StripeAccountDetails'],
  endpoints: (builder) => ({
    /**
     * Fetch all Stripe accounts for an organization
     */
    getOrganizationStripeAccounts: builder.query<StripeAccountsDashboardData, string>({
      queryFn: async (organizationId) => {
        try {
          console.log('[OrgStripeAPI] Fetching accounts for org:', organizationId);

          const orgRef = doc(DB, 'organisers', organizationId);
          const orgDoc = await getDoc(orgRef);

          if (!orgDoc.exists()) {
            return {
              error: {
                status: 'NOT_FOUND',
                error: `Organization ${organizationId} not found`,
              },
            };
          }

          const orgData = orgDoc.data();
          const accounts: OrgStripeAccount[] = orgData.stripeAccounts || [];

          // Calculate statistics
          const activeAccounts = accounts.filter((acc) => acc.status === 'active').length;
          const pendingAccounts = accounts.filter((acc) => acc.status === 'pending').length;
          const inactiveAccounts = accounts.filter(
            (acc) => acc.status === 'inactive' || !acc.isActive
          ).length;

          const primaryAccount = accounts.find((acc) => acc.isPrimary);

          const dashboardData: StripeAccountsDashboardData = {
            accounts,
            primaryAccount,
            totalAccounts: accounts.length,
            activeAccounts,
            pendingAccounts,
            inactiveAccounts,
          };

          console.log('[OrgStripeAPI] Dashboard data:', dashboardData);
          return { data: dashboardData };
        } catch (error: any) {
          console.error('[OrgStripeAPI] Error fetching accounts:', error);
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error.message,
            },
          };
        }
      },
      providesTags: ['OrgStripeAccounts'],
    }),

    /**
     * Create a new Stripe Connect account for an organization
     */
    createOrganizationStripeAccount: builder.mutation<
      CreateOrgStripeAccountResponse,
      CreateOrgStripeAccountRequest
    >({
      queryFn: async (request) => {
        try {
          console.log('[OrgStripeAPI] Creating Stripe account:', request);

          // Call cloud function to create Stripe account
          const createStripeAccount = httpsCallable<any, any>(FUNCTIONS, 'createStripeAccount');

          const result = await createStripeAccount({
            email: request.email,
            userid: request.userId,
            country: request.country || 'FR',
          });

          const response = result.data;

          if (response.hasError) {
            return {
              data: {
                hasError: true,
                error: response.error || 'Failed to create Stripe account',
              },
            };
          }

          // Generate unique local ID for this account record
          const accountRecordId = `stripe_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

          // Create account record
          const newAccount: OrgStripeAccount = {
            id: accountRecordId,
            accountId: response.accountId,
            accountName: request.accountName,
            description: request.description,
            status: 'pending' as StripeAccountStatus,
            capabilities: {
              charges_enabled: false,
              payouts_enabled: false,
            },
            country: request.country || 'FR',
            currency: 'EUR',
            isPrimary: false, // Will be set to true if it's the first account
            isActive: true,
            createdAt: Timestamp.now(), // Use Timestamp.now() for arrayUnion
            createdBy: request.userId,
            details_submitted: false,
          };

          // Add to organization's stripeAccounts array
          const orgRef = doc(DB, 'organisers', request.organizationId);
          const orgDoc = await getDoc(orgRef);

          if (!orgDoc.exists()) {
            return {
              data: {
                hasError: true,
                error: 'Organization not found',
              },
            };
          }

          const orgData = orgDoc.data();
          const existingAccounts = orgData.stripeAccounts || [];

          // If this is the first account, make it primary
          if (existingAccounts.length === 0) {
            newAccount.isPrimary = true;
          }

          await updateDoc(orgRef, {
            stripeAccounts: arrayUnion(newAccount),
            ...(newAccount.isPrimary && { primaryStripeAccountId: accountRecordId }),
            updatedAt: serverTimestamp(),
          });

          // Generate onboarding link
          const createAccountLink = httpsCallable<any, any>(
            FUNCTIONS,
            'createStripeAccountLink'
          );

          const linkResult = await createAccountLink({
            accountId: response.accountId,
            returnUrl: `${window.location.origin}/dashboard/evenementiel/orgs/${request.organizationId}/view`,
          });

          const linkResponse = linkResult.data;

          console.log('[OrgStripeAPI] Account created successfully:', accountRecordId);

          return {
            data: {
              hasError: false,
              accountId: response.accountId,
              accountRecordId,
              accountLinkUrl: linkResponse.accountLink?.url,
            },
          };
        } catch (error: any) {
          console.error('[OrgStripeAPI] Error creating account:', error);
          return {
            data: {
              hasError: true,
              error: error.message,
            },
          };
        }
      },
      invalidatesTags: ['OrgStripeAccounts'],
    }),

    /**
     * Update an existing Stripe account
     */
    updateOrganizationStripeAccount: builder.mutation<
      { success: boolean },
      UpdateOrgStripeAccountRequest
    >({
      queryFn: async (request) => {
        try {
          console.log('[OrgStripeAPI] Updating account:', request);

          const orgRef = doc(DB, 'organisers', request.organizationId);
          const orgDoc = await getDoc(orgRef);

          if (!orgDoc.exists()) {
            return {
              error: {
                status: 'NOT_FOUND',
                error: 'Organization not found',
              },
            };
          }

          const orgData = orgDoc.data();
          const accounts: OrgStripeAccount[] = orgData.stripeAccounts || [];

          // Find and update the account
          const accountIndex = accounts.findIndex((acc) => acc.id === request.accountRecordId);

          if (accountIndex === -1) {
            return {
              error: {
                status: 'NOT_FOUND',
                error: 'Account not found',
              },
            };
          }

          const updatedAccount = {
            ...accounts[accountIndex],
            ...(request.accountName && { accountName: request.accountName }),
            ...(request.description !== undefined && { description: request.description }),
            ...(request.isActive !== undefined && { isActive: request.isActive }),
            updatedAt: Timestamp.now(), // Use Timestamp.now() for array updates
          };

          accounts[accountIndex] = updatedAccount;

          await updateDoc(orgRef, {
            stripeAccounts: accounts,
            updatedAt: serverTimestamp(),
          });

          console.log('[OrgStripeAPI] Account updated successfully');
          return { data: { success: true } };
        } catch (error: any) {
          console.error('[OrgStripeAPI] Error updating account:', error);
          return {
            error: {
              status: 'UPDATE_ERROR',
              error: error.message,
            },
          };
        }
      },
      invalidatesTags: ['OrgStripeAccounts'],
    }),

    /**
     * Set primary Stripe account for an organization
     */
    setPrimaryStripeAccount: builder.mutation<{ success: boolean }, SetPrimaryStripeAccountRequest>(
      {
        queryFn: async (request) => {
          try {
            console.log('[OrgStripeAPI] Setting primary account:', request);

            const orgRef = doc(DB, 'organisers', request.organizationId);
            const orgDoc = await getDoc(orgRef);

            if (!orgDoc.exists()) {
              return {
                error: {
                  status: 'NOT_FOUND',
                  error: 'Organization not found',
                },
              };
            }

            const orgData = orgDoc.data();
            const accounts: OrgStripeAccount[] = orgData.stripeAccounts || [];

            // Unset all isPrimary flags
            const updatedAccounts = accounts.map((acc) => ({
              ...acc,
              isPrimary: acc.id === request.accountRecordId,
            }));

            await updateDoc(orgRef, {
              stripeAccounts: updatedAccounts,
              primaryStripeAccountId: request.accountRecordId,
              updatedAt: serverTimestamp(),
            });

            console.log('[OrgStripeAPI] Primary account set successfully');
            return { data: { success: true } };
          } catch (error: any) {
            console.error('[OrgStripeAPI] Error setting primary account:', error);
            return {
              error: {
                status: 'UPDATE_ERROR',
                error: error.message,
              },
            };
          }
        },
        invalidatesTags: ['OrgStripeAccounts'],
      }
    ),

    /**
     * Deactivate/delete a Stripe account
     */
    deactivateStripeAccount: builder.mutation<{ success: boolean }, DeactivateStripeAccountRequest>(
      {
        queryFn: async (request) => {
          try {
            console.log('[OrgStripeAPI] Deactivating account:', request);

            const orgRef = doc(DB, 'organisers', request.organizationId);
            const orgDoc = await getDoc(orgRef);

            if (!orgDoc.exists()) {
              return {
                error: {
                  status: 'NOT_FOUND',
                  error: 'Organization not found',
                },
              };
            }

            const orgData = orgDoc.data();
            const accounts: OrgStripeAccount[] = orgData.stripeAccounts || [];

            // Find and deactivate the account
            const accountIndex = accounts.findIndex((acc) => acc.id === request.accountRecordId);

            if (accountIndex === -1) {
              return {
                error: {
                  status: 'NOT_FOUND',
                  error: 'Account not found',
                },
              };
            }

            // Don't allow deactivating the primary account if there are other active accounts
            if (accounts[accountIndex].isPrimary && accounts.length > 1) {
              const otherActiveAccounts = accounts.filter(
                (acc) => acc.id !== request.accountRecordId && acc.isActive
              );
              if (otherActiveAccounts.length > 0) {
                return {
                  error: {
                    status: 'VALIDATION_ERROR',
                    error: 'Cannot deactivate primary account. Set another account as primary first.',
                  },
                };
              }
            }

            accounts[accountIndex] = {
              ...accounts[accountIndex],
              isActive: false,
              status: 'inactive' as StripeAccountStatus,
              updatedAt: Timestamp.now(), // Use Timestamp.now() for array updates
            };

            await updateDoc(orgRef, {
              stripeAccounts: accounts,
              updatedAt: serverTimestamp(),
            });

            console.log('[OrgStripeAPI] Account deactivated successfully');
            return { data: { success: true } };
          } catch (error: any) {
            console.error('[OrgStripeAPI] Error deactivating account:', error);
            return {
              error: {
                status: 'UPDATE_ERROR',
                error: error.message,
              },
            };
          }
        },
        invalidatesTags: ['OrgStripeAccounts'],
      }
    ),

    /**
     * Sync account status from Stripe
     */
    syncStripeAccountStatus: builder.mutation<
      { success: boolean; account?: any },
      SyncStripeAccountRequest
    >({
      queryFn: async (request) => {
        try {
          console.log('[OrgStripeAPI] Syncing account status:', request);

          const orgRef = doc(DB, 'organisers', request.organizationId);
          const orgDoc = await getDoc(orgRef);

          if (!orgDoc.exists()) {
            return {
              error: {
                status: 'NOT_FOUND',
                error: 'Organization not found',
              },
            };
          }

          const orgData = orgDoc.data();
          const accounts: OrgStripeAccount[] = orgData.stripeAccounts || [];

          const accountIndex = accounts.findIndex((acc) => acc.id === request.accountRecordId);

          if (accountIndex === -1) {
            return {
              error: {
                status: 'NOT_FOUND',
                error: 'Account not found',
              },
            };
          }

          const account = accounts[accountIndex];

          // Call cloud function to get account details from Stripe
          const getStripeAccount = httpsCallable<any, any>(FUNCTIONS, 'stripeconnect-getStripeAccountDetails');

          const result = await getStripeAccount({
            accountId: account.accountId,
          });

          const response = result.data as StripeAccountDetailsResponse;

          if (response.hasError || !response.account) {
            return {
              error: {
                status: 'FETCH_ERROR',
                error: response.error || 'Failed to fetch account details from Stripe',
              },
            };
          }

          const stripeAccount = response.account;

          // Update local account with Stripe data
          accounts[accountIndex] = {
            ...account,
            status:
              stripeAccount.charges_enabled && stripeAccount.payouts_enabled
                ? ('active' as StripeAccountStatus)
                : ('pending' as StripeAccountStatus),
            capabilities: {
              charges_enabled: stripeAccount.charges_enabled,
              payouts_enabled: stripeAccount.payouts_enabled,
            },
            details_submitted: stripeAccount.details_submitted,
            country: stripeAccount.country,
            defaultCurrency: stripeAccount.default_currency,
            email: stripeAccount.email,
            business_type: stripeAccount.business_type,
            lastSyncedAt: Timestamp.now(), // Use Timestamp.now() for array updates
            updatedAt: Timestamp.now(), // Use Timestamp.now() for array updates
          };

          await updateDoc(orgRef, {
            stripeAccounts: accounts,
            updatedAt: serverTimestamp(),
          });

          console.log('[OrgStripeAPI] Account synced successfully');
          return { data: { success: true, account: accounts[accountIndex] } };
        } catch (error: any) {
          console.error('[OrgStripeAPI] Error syncing account:', error);
          return {
            error: {
              status: 'SYNC_ERROR',
              error: error.message,
            },
          };
        }
      },
      invalidatesTags: ['OrgStripeAccounts', 'StripeAccountDetails'],
    }),

    /**
     * Get the active Stripe account for payments
     * Priority: Event-specific > Organization primary > User legacy
     */
    getActiveStripeAccount: builder.query<GetActiveAccountResponse, GetActiveAccountRequest>({
      queryFn: async (request) => {
        try {
          console.log('[OrgStripeAPI] Getting active account:', request);

          // 1. Check for event-specific account (future feature)
          if (request.eventId) {
            // TODO: Implement event-specific account selection
            console.log('[OrgStripeAPI] Event-specific accounts not yet implemented');
          }

          // 2. Check organization's primary account
          const orgRef = doc(DB, 'organisers', request.organizationId);
          const orgDoc = await getDoc(orgRef);

          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            const accounts: OrgStripeAccount[] = orgData.stripeAccounts || [];
            const primaryAccountId = orgData.primaryStripeAccountId;

            // Find primary account
            const primaryAccount = accounts.find(
              (acc) => acc.id === primaryAccountId && acc.isActive && acc.status === 'active'
            );

            if (primaryAccount) {
              return {
                data: {
                  hasError: false,
                  accountId: primaryAccount.accountId,
                  accountName: primaryAccount.accountName,
                  source: 'organization_primary',
                  isPrimary: true,
                },
              };
            }

            // Find any active account if no primary
            const anyActiveAccount = accounts.find((acc) => acc.isActive && acc.status === 'active');

            if (anyActiveAccount) {
              return {
                data: {
                  hasError: false,
                  accountId: anyActiveAccount.accountId,
                  accountName: anyActiveAccount.accountName,
                  source: 'organization_default',
                  isPrimary: false,
                },
              };
            }

            // Check if we should fallback to user account
            if (orgData.useLegacyUserAccount !== false && request.userId) {
              // 3. Fallback to user's legacy account
              const userRef = doc(DB, 'users', request.userId);
              const userDoc = await getDoc(userRef);

              if (userDoc.exists()) {
                const userData = userDoc.data();
                if (
                  userData.stripeConnect?.accountId &&
                  userData.stripeConnect?.status === 'active'
                ) {
                  return {
                    data: {
                      hasError: false,
                      accountId: userData.stripeConnect.accountId,
                      accountName: 'User Account (Legacy)',
                      source: 'user_legacy',
                      isPrimary: false,
                    },
                  };
                }
              }
            }
          }

          // No active account found
          return {
            error: {
              status: 'NOT_FOUND',
              error: 'No active Stripe account found for this organization',
            },
          };
        } catch (error: any) {
          console.error('[OrgStripeAPI] Error getting active account:', error);
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error.message,
            },
          };
        }
      },
      providesTags: ['OrgStripeAccounts'],
    }),
  }),
});

export const {
  useGetOrganizationStripeAccountsQuery,
  useCreateOrganizationStripeAccountMutation,
  useUpdateOrganizationStripeAccountMutation,
  useSetPrimaryStripeAccountMutation,
  useDeactivateStripeAccountMutation,
  useSyncStripeAccountStatusMutation,
  useGetActiveStripeAccountQuery,
} = organizationStripeApi;
